import axios, {
  AxiosError,
  type AxiosInstance,
  type AxiosRequestConfig,
  type AxiosResponse,
  type InternalAxiosRequestConfig,
} from 'axios';

import { uuidv4 } from '@/lib/utils';
import { useAuthStore } from '@/lib/auth/store';
import { CubixApiError, type ApiResponse } from './types';

const API_VERSION = import.meta.env.VITE_API_VERSION ?? 'v1';

/**
 * Build the canonical CUBIX API URL.
 *
 *   apiPath('alm', 'gaps', tenantId)  →  /api/v2/alm/gaps/<tenantId>
 *
 * Reflects §3.1 — /api/{version}/{domain}/{resource}/{identifier?}/{sub-resource?}.
 */
export function apiPath(...segments: Array<string | number | undefined>): string {
  const parts = segments.filter((s): s is string | number => s !== undefined && s !== '');
  return `/api/${API_VERSION}/${parts.join('/')}`;
}

/**
 * Single axios instance used by every domain-specific client.
 *
 * Interceptors enforce the architecture rules:
 *   - JWT: Authorization: Bearer <token>
 *   - X-Trace-ID: generated client-side if absent (gateway will pass through)
 *   - X-Request-ID: idempotency key, MANDATORY on POST/PUT/PATCH
 *
 * 401 responses trigger a forced logout — the user must re-authenticate.
 */
export const httpClient: AxiosInstance = axios.create({
  baseURL: '/',
  timeout: 30_000,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
});

httpClient.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const state = useAuthStore.getState();
  const token = state.token;
  if (token) {
    config.headers.set('Authorization', `Bearer ${token}`);
  }

  // Dev shortcut: cx-alm currently reads X-Tenant-ID when no JWT is present
  // (see AlmController). When JWT is in use the server's TenantInterceptor
  // reads tenant_id from the token and ignores this header — safe to always
  // send. Falls back to the configured default if the user isn't logged in.
  const tenantId =
    state.user?.tenant_id ??
    (import.meta.env.VITE_DEFAULT_TENANT as string | undefined) ??
    null;
  if (tenantId && !config.headers.get('X-Tenant-ID')) {
    config.headers.set('X-Tenant-ID', tenantId);
  }

  if (!config.headers.get('X-Trace-ID')) {
    config.headers.set('X-Trace-ID', uuidv4());
  }

  const method = config.method?.toUpperCase();
  if (method && ['POST', 'PUT', 'PATCH'].includes(method)) {
    if (!config.headers.get('X-Request-ID')) {
      config.headers.set('X-Request-ID', uuidv4());
    }
  }

  return config;
});

httpClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError<ApiResponse<unknown>>) => {
    if (error?.response?.status === 401) {
      useAuthStore.getState().logout();
    }
    return Promise.reject(error);
  },
);

/**
 * Unwrap the cx-commons `ApiResponse<T>` success envelope. On a wire error
 * (axios threw because of non-2xx) translate the server's error envelope
 * into a `CubixApiError` carrying the code and trace_id for the UI.
 */
function unwrap<T>(response: AxiosResponse<ApiResponse<T>>): T {
  const body = response.data;
  if (!body?.success) {
    throw new CubixApiError(
      body?.error?.message ?? body?.message ?? 'Unexpected server response',
      body?.error?.code ?? 'UNEXPECTED',
      response.status,
      { traceId: body?.error?.traceId, field: body?.error?.field },
    );
  }
  // `data` is `@JsonInclude(NON_NULL)` — endpoints that return nothing serialize
  // it as undefined. The cast to `T` is intentional; void endpoints type T as void.
  return body.data as T;
}

function toCubixError(err: unknown): never {
  if (err instanceof CubixApiError) throw err;
  if (axios.isAxiosError(err)) {
    const body = err.response?.data as ApiResponse<unknown> | undefined;
    throw new CubixApiError(
      body?.error?.message ?? err.message,
      body?.error?.code ?? `HTTP_${err.response?.status ?? 'NETWORK'}`,
      err.response?.status ?? 0,
      { traceId: body?.error?.traceId, field: body?.error?.field },
    );
  }
  throw err;
}

/**
 * Thin helpers that unwrap the standard envelope and return `data` directly.
 * Components and hooks should use these in 99% of cases.
 */
export async function apiGet<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
  try {
    return unwrap(await httpClient.get<ApiResponse<T>>(url, config));
  } catch (err) {
    toCubixError(err);
  }
}

export async function apiPost<T, B = unknown>(
  url: string,
  body: B,
  config?: AxiosRequestConfig,
): Promise<T> {
  try {
    return unwrap(await httpClient.post<ApiResponse<T>>(url, body, config));
  } catch (err) {
    toCubixError(err);
  }
}

export async function apiPut<T, B = unknown>(
  url: string,
  body: B,
  config?: AxiosRequestConfig,
): Promise<T> {
  try {
    return unwrap(await httpClient.put<ApiResponse<T>>(url, body, config));
  } catch (err) {
    toCubixError(err);
  }
}

export async function apiDelete<T = void>(
  url: string,
  config?: AxiosRequestConfig,
): Promise<T> {
  try {
    return unwrap(await httpClient.delete<ApiResponse<T>>(url, config));
  } catch (err) {
    toCubixError(err);
  }
}

/**
 * Multipart file upload. Pass a `File` (and optional `extra` form fields);
 * the browser sets the Content-Type to multipart/form-data with the
 * correct boundary automatically. Used by Phase 2.0.NIM.v1 to ship the
 * yield-curve xlsx.
 *
 * IMPORTANT: the shared httpClient has `Content-Type: application/json`
 * baked in as a default. We MUST clear that header on this request so
 * the browser/axios can populate it with `multipart/form-data; boundary=...`
 * — otherwise Tomcat downstream rejects the request as non-multipart.
 */
export async function apiUpload<T>(
  url: string,
  file: File,
  extra: Record<string, string | number | boolean | undefined> = {},
  fieldName = 'file',
): Promise<T> {
  const form = new FormData();
  form.append(fieldName, file);
  for (const [k, v] of Object.entries(extra)) {
    if (v !== undefined && v !== null) form.append(k, String(v));
  }
  try {
    return unwrap(await httpClient.post<ApiResponse<T>>(url, form, {
      // Setting to undefined lets axios + the browser pick the multipart
      // boundary instead of inheriting the JSON default from the instance.
      headers: { 'Content-Type': undefined as unknown as string },
    }));
  } catch (err) {
    toCubixError(err);
  }
}
